import { projectImages } from '../assets/images/imageConfig';

/**
 * Catalogue unique des projets de la plateforme (montants en FCFA).
 * Utilisé par la page Projets, la page Investissement, le profil, les notifications et les statistiques
 * (src/services/platformStats.js). impact.beneficiaires : uniquement si la description le précise.
 * highlights (facultatif) : [{ icon, title, text }], points clés affichés en cartes sur la fiche du projet.
 * category : un secteur de la liste unique (src/config/sectors.js) ; secteur : précision libre.
 * imageCredit (facultatif) : crédit de la photo, affiché sous la photo de la fiche du projet.
 * porteur (facultatif) : { nom, role, presentation, photo } — affiché sur la fiche du projet dès qu'il est
 *   renseigné (role : « Présidente du groupement… » ; photo : vraie photo, ex. '/assets/media/porteurs/nom.jpg',
 *   avec l'accord de la personne). Sans porteur, la fiche invite à poser ses questions à l'équipe.
 * finCollecte (facultatif) : date limite de collecte « AAAA-MM-JJ ». Affiche « X jours restants » sur les cartes
 *   et la fiche ; après cette date, le projet n'accepte plus de nouveaux investissements.
 * Les photos, rapports et emplois créés sont publiés par l'équipe (Administration → Rapports).
 */
// Porteur des projets du catalogue : projets portés et suivis par C.I.I (à remplacer, projet par projet,
// par l'entrepreneur ou le groupement qui mène le projet, avec son accord)
const PORTEUR_CII = {
  nom: 'Community Impact-Invest (C.I.I)',
  role: 'Responsable : Garba Amadou Issaka, Président-fondateur',
  presentation: "Projet porté et suivi directement par l'équipe de Community Impact-Invest, à Zinder : sélection des bénéficiaires, achats, suivi sur le terrain et rapports publiés à chaque étape."
};

// Date limite de collecte commune : 90 jours après la mise en ligne du 27 octobre 2026
const FIN_COLLECTE = '2027-01-25';

export const defaultProjects = [
  {
    id: 'static-1',
    titre: "Ferme Écologique Intégrée Moderne",
    description: "Ferme intégrée dans la région de Tillabéri, où cultures, élevage et arbres se complètent. En saison des pluies : mil, sorgho et niébé. En contre-saison : oignon, tomate, chou, piment et moringa, arrosés au goutte-à-goutte grâce à un forage équipé d'une pompe solaire. L'embouche ovine et la volaille fournissent le fumier, transformé en compost : la ferme produit sans engrais ni pesticides chimiques. Zaï, demi-lunes et haies vives retiennent l'eau et protègent les sols contre l'érosion. La ferme sert aussi d'école : 200 agriculteurs des villages voisins y sont formés à ces méthodes, puis les appliquent dans leurs propres champs. Les récoltes sont vendues sur les marchés locaux et à Niamey.",
    imageUrl: projectImages.agricultureBio.src,
    videoUrl: "https://www.youtube.com/embed/Oz5W6QElvTQ",
    coutTotal: 20000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.08,
    category: 'Agriculture et élevage',
    location: "Tillabéri, Niger",
    isFeatured: true,
    secteur: "Agriculture Durable",
    duree: "24 mois",
    risque: "Faible",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2024-01-15",
    impactResume: "200 agriculteurs formés, 50 hectares en agroécologie",
    beneficiaires: "Agriculteurs, groupements de femmes maraîchères et coopératives des villages voisins",
    // Bénéficiaires visés, repris de la description du projet (utilisés dans les statistiques)
    impact: { beneficiaires: 200, libelle: 'agriculteurs formés' }
  },
  {
    id: 'static-2',
    titre: "Centre Hub Technologique & Incubateur des Jeunes",
    description: "Centre à Niamey où les jeunes sont encadrés, formés et coachés pour créer leur propre entreprise : PME, très petites entreprises, coopératives et activités génératrices de revenus. Chaque jeune est suivi pas à pas, de l'idée jusqu'au lancement de son entreprise, puis pendant ses premiers mois d'activité. Le centre réunit un espace de travail partagé, connecté à internet et équipé d'une installation solaire avec batteries pour continuer à fonctionner pendant les coupures d'électricité, une salle de formation et un programme d'incubation. Formations pratiques : bureautique, maintenance informatique et réparation de téléphones, création de sites web et d'applications mobiles, marketing digital et Mobile Money pour les commerces. Incubation : 50 jeunes porteurs de projets accompagnés pour bâtir leur plan d'affaires, s'immatriculer au registre du commerce (RCCM), tenir leur comptabilité et trouver leurs premiers clients, avec l'aide de mentors ; les projets utiles à l'agriculture, au commerce et aux services de proximité sont privilégiés, et des places sont réservées aux jeunes femmes. Le centre vit de ses propres recettes : abonnements à l'espace de travail, frais de formation, location de la salle et services numériques aux entreprises et associations (sites web, outils de gestion). Les fonds financent l'aménagement et l'équipement du local (ordinateurs, connexion, installation solaire, mobilier) et les premiers mois de fonctionnement du programme.",
    imageUrl: projectImages.startupTech.src,
    videoUrl: "https://www.youtube.com/embed/z1e3k_Tv3eA",
    coutTotal: 50000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.1,
    category: 'Numérique',
    location: "Niamey, Niger",
    isFeatured: true,
    secteur: "Numérique et incubation",
    duree: "36 mois",
    risque: "Modéré",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2024-03-01",
    impactResume: "50 jeunes encadrés, formés et coachés pour créer leur entreprise (PME, TPE, coopératives), 200 emplois visés",
    beneficiaires: "Jeunes de 18 à 35 ans, diplômés ou non, et jeunes femmes entrepreneures",
    impact: { beneficiaires: 50, libelle: 'jeunes encadrés pour créer leur entreprise' }
  },
  {
    id: 'static-3',
    titre: "Centre de Formation Numérique",
    description: "Centre de formation aux métiers du numérique et de l'entrepreneuriat. Formation de 400 jeunes par an aux compétences digitales et création d'entreprise.",
    imageUrl: projectImages.centreFormation.src,
    videoUrl: "",
    coutTotal: 30000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.06,
    category: 'Éducation et formation',
    location: "Maradi, Niger",
    isFeatured: false,
    secteur: "Éducation",
    duree: "18 mois",
    risque: "Faible",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2024-02-01",
    impactResume: "400 jeunes formés par an",
    beneficiaires: "Jeunes de 18-35 ans",
    impact: { beneficiaires: 400, libelle: 'jeunes formés par an' }
  },
  {
    id: 'static-4',
    titre: "Réseau de Microfinance Inclusive",
    description: "Le réseau qui aide les femmes et les jeunes à épargner, à financer leurs activités sans intérêt et à vendre sur les marchés régionaux.",
    // Points clés affichés en cartes sur la fiche (icônes : mobile, handshake, users, market, prices, online)
    highlights: [
      { icon: 'mobile', title: 'Services financiers mobiles', text: 'Épargne et paiements par Mobile Money, financement sans intérêt.' },
      { icon: 'handshake', title: 'Mise en relation', text: 'Acheteurs, fournisseurs et transporteurs.' },
      { icon: 'users', title: 'Réseau de membres', text: 'Rencontres, achats et ventes en commun.' },
      { icon: 'market', title: 'Marchés régionaux', text: 'Foires, grands marchés, Nigeria et Bénin.' },
      { icon: 'prices', title: 'Prix des marchés', text: 'Partagés par téléphone pour vendre au bon prix.' },
      { icon: 'online', title: 'Vente en ligne', text: 'De nouveaux clients grâce aux plateformes en ligne.' }
    ],
    imageUrl: projectImages.microfinance.src,
    videoUrl: "",
    coutTotal: 10000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.05,
    // Mise en relation et accès aux marchés : classé dans le commerce (secteur précisé ci-dessous)
    category: 'Commerce',
    location: "Dosso, Niger",
    isFeatured: false,
    secteur: "Finance inclusive et accès aux marchés",
    duree: "12 mois",
    risque: "Faible",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2024-04-01",
    impactResume: "Services financiers mobiles, mise en relation commerciale et accès aux marchés régionaux pour les femmes et les jeunes",
    beneficiaires: "Femmes et jeunes entrepreneurs, groupements et coopératives"
  },
  {
    id: 'static-5',
    titre: "Élevage Avicole de Zinder",
    // Chiffres repris du plan d'affaires du projet (500 poulets par mois, charges de 875 000 FCFA par cycle)
    description: "Élevage de poulets à Zinder : production de 500 poulets par mois, vendus sur les marchés locaux. Les fonds financent le fonds de roulement (poussins, aliment, soins vétérinaires) et les équipements de l'élevage (bâtiment, mangeoires, abreuvoirs).",
    imageUrl: "/assets/media/projet-avicole-zinder.jpg",
    // Vidéo hébergée sur le site (fichier MP4), lue directement sur la fiche du projet
    videoUrl: "/assets/media/projet-avicole-zinder.mp4",
    coutTotal: 5000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.1,
    category: 'Agriculture et élevage',
    location: "Zinder, Niger",
    isFeatured: true,
    secteur: "Aviculture",
    duree: "12 mois",
    risque: "Modéré",
    finCollecte: FIN_COLLECTE,
    startDate: "2026-10-03",
    dateCreated: "2026-10-03T08:00:00Z",
    impactResume: "500 poulets produits chaque mois pour les marchés de Zinder",
    porteur: PORTEUR_CII
  },
  {
    id: 'static-6',
    titre: "Kits Solaires pour Foyers et Boutiques",
    description: "Vente et installation de kits solaires (lampes, panneaux, batteries) pour les ménages, les boutiques et les villages. Au Niger, beaucoup de foyers n'ont pas l'électricité, surtout en zone rurale, et en ville les coupures sont fréquentes, en particulier pendant la saison chaude. Trois gammes sont proposées : la lampe solaire qui recharge aussi le téléphone, le kit familial (panneau, batterie, ampoules, prise pour radio ou ventilateur) et le kit boutique, capable d'alimenter un réfrigérateur ou un congélateur pour vendre de l'eau fraîche et de la glace. Les clients peuvent payer en plusieurs fois par Mobile Money, à un prix fixé d'avance et sans intérêt. Des techniciens formés sur place assurent l'installation, la garantie et l'entretien : nettoyage des panneaux après les vents de poussière, remplacement des batteries. Les fonds financent le stock de kits, l'outillage et la formation des techniciens.",
    imageUrl: projectImages.energieSolaire.src,
    videoUrl: "",
    coutTotal: 10000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.09,
    category: 'Énergie',
    location: "Niamey, Niger",
    isFeatured: true,
    secteur: "Énergie Solaire",
    duree: "18 mois",
    risque: "Modéré",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2026-10-11",
    dateCreated: "2026-10-11T08:00:00Z",
    impactResume: "Éclairage et électricité solaires pour les foyers sans courant et les boutiques touchées par les coupures",
    beneficiaires: "Ménages, boutiques et villages"
  },
  {
    id: 'static-7',
    titre: "Embouche Ovine et Bovine",
    description: "Engraissement de moutons et de bœufs achetés maigres sur les marchés à bétail, puis revendus en bon état trois à quatre mois plus tard. Les ventes sont très fortes à la Tabaski, où chaque famille cherche son mouton, et les moutons du Niger sont exportés vers les autres pays de la sous-région. Les animaux sont nourris au son, au tourteau de coton, aux fanes de niébé et d'arachide et à la paille, avec des pierres à lécher. Un agent vétérinaire assure la vaccination, le déparasitage et le suivi sanitaire. Les cycles d'engraissement sont calés sur la Tabaski et les grandes fêtes pour vendre au meilleur moment. Les fonds financent l'achat des animaux, l'aliment, les soins vétérinaires et l'aménagement de l'enclos (abri, mangeoires, abreuvoirs).",
    // Photo d'illustration (marché à bétail de N'Gonga, Niger), licence libre : le crédit est obligatoire
    imageUrl: "/assets/media/projet-embouche-marche-betail.jpg",
    imageCredit: "Photo d'illustration : marché à bétail de N'Gonga, Niger. NigerTZai, Wikimedia Commons, CC BY-SA 4.0.",
    videoUrl: "",
    coutTotal: 10000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.1,
    category: 'Agriculture et élevage',
    location: "Maradi, Niger",
    isFeatured: true,
    secteur: "Embouche",
    duree: "12 mois",
    risque: "Modéré",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2026-10-11",
    dateCreated: "2026-10-11T08:00:00Z",
    impactResume: "Moutons et bœufs engraissés pour la Tabaski, les marchés locaux et l'exportation",
    beneficiaires: "Éleveurs, vendeurs d'aliment bétail et familles à la Tabaski"
  },
  {
    id: 'static-8',
    titre: "Fabrique d'Aliments pour le Bétail",
    description: "Production d'aliments pour le bétail : tourteaux, son et blocs multinutritionnels. Chaque saison sèche, les pâturages s'épuisent, l'aliment manque et son prix s'envole ; les éleveurs voient leurs animaux maigrir ou doivent les vendre à perte. L'unité achète les matières premières à la récolte, quand elles sont abondantes et bon marché (graines de coton, son de mil et de riz, fanes de niébé et d'arachide, tiges de mil), les broie, les mélange et les stocke pour les vendre pendant la période de pénurie. Les blocs multinutritionnels, à lécher, apportent sel, minéraux et azote aux animaux nourris à la paille. L'aliment est vendu en sacs de 50 kg et en petits conditionnements, à la portée des petits éleveurs. Les fonds financent le broyeur, le mélangeur, la presse à blocs, le magasin de stockage et le premier stock de matières premières.",
    // Photo d'illustration (broyeur d'aliments bétail à Maradi), licence libre : le crédit est obligatoire
    imageUrl: "/assets/media/projet-aliment-betail-broyeur.jpg",
    imageCredit: "Photo d'illustration : broyeur d'aliments pour le bétail à Maradi, Niger. Tawaye, Wikimedia Commons, CC BY-SA 4.0.",
    videoUrl: "",
    coutTotal: 10000000,
    montantRecu: 0,
    membres: 0,
    tauxRetour: 0.09,
    category: 'Agriculture et élevage',
    location: "Tahoua, Niger",
    isFeatured: true,
    secteur: "Alimentation Animale",
    duree: "18 mois",
    risque: "Modéré",
    finCollecte: FIN_COLLECTE,
    porteur: PORTEUR_CII,
    startDate: "2026-10-11",
    dateCreated: "2026-10-11T08:00:00Z",
    impactResume: "De l'aliment disponible pour les troupeaux pendant la saison sèche",
    beneficiaires: "Éleveurs, emboucheurs et producteurs de lait"
  },
];

/**
 * Projets publiés (catalogue). Les projets présentés par les entrepreneurs ne sont pas publiés
 * directement : ils sont examinés par l'équipe (src/pages/SubmitProject.js → messages de l'administration),
 * puis ajoutés au catalogue (ou à la table projects de Supabase) une fois validés.
 */
export function getProjects() {
  return defaultProjects;
}

/**
 * Identifiant d'un projet du catalogue dans la table projects de Supabase :
 * « static-3 » ↔ « 00000000-0000-4000-8000-000000000003 » (lignes créées par database/INSTALLER-TOUT.sql).
 * Les autres identifiants (projets ajoutés directement dans la base) sont rendus tels quels.
 */
const DB_ID_PREFIX = '00000000-0000-4000-8000-';

export function projectDbId(id) {
  const match = /^static-(\d{1,12})$/.exec(String(id));
  return match ? `${DB_ID_PREFIX}${match[1].padStart(12, '0')}` : String(id);
}

export function projectSiteId(dbId) {
  const value = String(dbId);
  return value.startsWith(DB_ID_PREFIX) && /^\d{12}$/.test(value.slice(DB_ID_PREFIX.length))
    ? `static-${Number(value.slice(DB_ID_PREFIX.length))}`
    : value;
}
